import { Component, OnInit, ViewChild, inject, output } from '@angular/core';
import { links } from 'src/app/core/links';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { UserContextService } from 'src/app/core/service/user-context.service';
import { UserRoleEnum } from 'src/app/core/enums/user-role.enums';
import { NavTabEnum } from 'src/app/core/enums/nav-tab.enum';
import { NgClass } from '@angular/common';
import { GlobalConstants } from 'src/app/core/global-constants';
import { environment } from 'src/environments/environment';
import { ArrayUtil } from 'src/app/core/utility/array.util';
import { filter } from 'rxjs';
import { YtdItemsSold } from 'src/app/core/models/ytd-items-sold.model';
import { ChartService } from 'src/app/core/service/chart.service';
import { FormattingUtil } from 'src/app/core/utility/formatting.util';

@Component({
    selector: 'sidebar-desktop',
    templateUrl: './sidebar-desktop.component.html',
    styleUrls: ['./sidebar-desktop.component.scss'],
    imports: [RouterLink, FormsModule, NgClass]
})
export class SideBarDesktopComponent implements OnInit {
    private context = inject(UserContextService);
    private router = inject(Router);
    private chartService = inject(ChartService);

    public readonly openSideBar = output<boolean>();

    @ViewChild("dropdownMenuBody", { static: true }) public dropdownBody: any;
    @ViewChild("dropdownMenu", { static: true }) public dropdown: any;

    public links = links;
    public companyName = 'LGFG';
    public role: UserRoleEnum;
    public documentLink = { name: 'Documents', href: links.documentsLink + '/' + links.documents.List };
    public actionLinks: ExtendedNavLink[] = [];
    public reportLinks: ExtendedNavLink[] = [];
    public adminSettings: ExtendedNavLink[] = null;
    public userName: string;
    public tabInFocus: NavTabEnum = 0;
    public enum = NavTabEnum;
    public currentPage: string;
    public isOpen = true;
    public isAdmin: boolean;
    public isCfo: boolean;
    public adminUrl = links.adminSettingsLink + '/' + links.adminSettings.mainPage;
    public isNumber = (value: any) => !Number.isNaN(Number(value));
    public ytdItemsSold: YtdItemsSold;
    public ncpRollover: string;
    public ncpCurrency: string;
    public showNcpRollover: boolean;
    public isBaseWorkspace: boolean;
    public averageSalesForLast3Months: string;
    public salesNeededToQualify: string;

    public onReportClick(link: ExtendedNavLink): void {
        link.isSelected = !link.isSelected;
    }

    public async ngOnInit(): Promise<void> {
        this.companyName = this.context.getWorkspaceName() || 'LGFG';
        document.title = `${this.companyName} CMS`;
        this.isAdmin = this.context.isAdmin();
        this.isCfo = this.context.isCfo();
        this.isBaseWorkspace = this.context.getWorkspaceId() === GlobalConstants.baseWorkspaceId;
        this.isOpen = localStorage.getItem(GlobalConstants.isOpenSideBarKey) == 'true';
        this.openSideBar.emit(this.isOpen);
        this.showNcpRollover = (this.isAdmin || this.context.isRep()) && this.isBaseWorkspace;
        this.setLinks();
        await this.setLgfgSummaries();
        this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe((val: any) => {
            if (val.url.includes("/SubmitPayment/") || val.url.includes("/MyGoal")) {
                this.setSideBarState();
            }
        });

    }

    public ngAfterContentInit(): void {
        this.setSideBarState();
        if (!this.isBaseWorkspace) {
            return;
        }
        setInterval(async () => {
            await this.setLgfgSummaries();
        }, 60000);
    }

    // LGFG (base workspace) only figures.
    private async setLgfgSummaries(): Promise<void> {
        if (!this.isBaseWorkspace) {
            return;
        }
        this.ytdItemsSold = await this.chartService.getYtdItemsSold();
        const ncpRollover = await this.chartService.getNcpRollover();
        this.ncpRollover = FormattingUtil.getCommaSeparatedNumber(Math.round(ncpRollover.value));
        this.ncpCurrency = ncpRollover.currency;
        await this.setAverageSalesSummary();
    }

    private async setAverageSalesSummary(): Promise<void> {
        const summary = await this.chartService.getAverageSalesSummary();
        this.averageSalesForLast3Months = summary.averageAmount == 0 ? '0' : FormattingUtil.getCommaSeparatedNumber(Math.round(summary.averageAmount));
        this.salesNeededToQualify = FormattingUtil.getCommaSeparatedNumber(Math.round(summary.minAmount));
    }

    public reportTabClick() {
        if (this.tabInFocus == 1 && this.isOpen) {
            this.tabInFocus = -1;
            return;
        }
        this.openSidebar();
        this.tabInFocus = 1;
    }

    public dashboardTabClick() {
        document.title = `${this.companyName} CMS`;
        this.tabInFocus = 0;
        this.currentPage = "";
    }

    public actionsTabClick() {
        if (this.tabInFocus == 2 && this.isOpen) {
            this.tabInFocus = -1;
            return;
        }
        this.openSidebar();
        this.tabInFocus = 2;
    }

    public administrationTabClick() {
        if (this.tabInFocus == 5 && this.isOpen) {
            this.tabInFocus = -1;
            return;
        }
        this.openSidebar();
        this.tabInFocus = 5;
    }

    public documentsTabClick() {
        if (this.tabInFocus == 4) {
            return;
        }
        this.currentPage = "";
        this.tabInFocus = 4;
    }

    public clickOnLink(link: ExtendedNavLink) {
        this.currentPage = link.href;
    }

    public toggleSidebar() {
        this.isOpen = !this.isOpen;
        localStorage.setItem(GlobalConstants.isOpenSideBarKey, this.isOpen.toString());
        this.openSideBar.emit(this.isOpen);
    }

    private openSidebar() {
        if (this.isOpen) {
            return;
        }

        this.isOpen = true;
        localStorage.setItem(GlobalConstants.isOpenSideBarKey, this.isOpen.toString());
        this.openSideBar.emit(this.isOpen);
    }

    private setLinks(): void {
        const roles = this.context.getRoles();
        if (this.context.isAdmin()) {
            this.setAdminLinks();
            return;
        }
        if (this.context.isAccounting()) {
            this.setAccountingLinks();
            return;
        }
        roles.forEach(role => {
            if (role == UserRoleEnum.ea) {
                this.setEaLinks();
            }
            if (role == UserRoleEnum.sm) {
                this.setSmLinks();
            }
            if (role == UserRoleEnum.bb) {
                this.setBbLinks();
            }
            if (role == UserRoleEnum.rep) {
                this.setRepLinks();
            }
            if (role == UserRoleEnum.alterationApprover) {
                this.reportLinks.push({ name: 'My Alterations Report', href: links.reportLink + '/' + links.reports.alterationsReport });
            }
            if (role == UserRoleEnum.expenseApprover) {
                this.reportLinks.push({ name: 'My Expenses Report', href: links.reportLink + '/' + links.reports.expensesReport });
            }
            if (role == UserRoleEnum.expenseSubmiter) {
                this.reportLinks.push({ name: 'My Expenses Report', href: links.reportLink + '/' + links.reports.expensesReport });
            }
        });
        if (this.actionLinks.length == 0) {
            this.actionLinks = null;
        }

        this.reportLinks = ArrayUtil.distinct(this.reportLinks, 'name');
        this.actionLinks = ArrayUtil.distinct(this.actionLinks, 'name');
    }

    private setAdminLinks(): void {
        this.reportLinks = [
            { name: 'My Sales Report', href: links.reportLink + '/' + links.reports.mySalesReport },
            { name: 'Wholesale Sales Report', href: links.reportLink + '/' + links.reports.wholesaleMySalesReport },
            { name: 'My Items Report', href: links.reportLink + '/' + links.reports.itemsReport },
            { name: 'Wholesale Items Report', href: links.reportLink + '/' + links.reports.wholesaleItemsReport },
            { name: 'My Cashflow Report', href: links.reportLink + '/' + links.reports.cashflowReport },
            { name: 'Wholesale Cashflow Report', href: links.reportLink + '/' + links.reports.wholesaleCashflowReport },
            { name: 'Total Clothing and Alterations Budget Bank Report', href: links.reportLink + '/' + links.reports.totalNCPBankReport },
            { name: 'My Alterations Report', href: links.reportLink + '/' + links.reports.alterationsReport },
            { name: 'My Expenses Report', href: links.reportLink + '/' + links.reports.expensesReport },
            { name: 'My Payments Report', href: links.reportLink + '/' + links.reports.paymentReport },
            { name: 'Rep Overdue Report', href: links.reportLink + '/' + links.reports.overdueReport },
            { name: 'Management Report', href: links.reportLink + '/' + links.reports.managementReport },
            { name: 'Rep Overview Report', href: links.reportLink + '/' + links.reports.overviewReport },
            { name: 'Cashflow Trailers Report', href: links.reportLink + '/' + links.reports.cashflowTrailorsReport },
            { name: 'Top Clients Report', href: links.reportLink + '/' + links.reports.topClientReport },
            { name: 'SM Report', href: links.reportLink + '/' + links.reports.socialMediaReport },
            { name: 'BB Report', href: links.reportLink + '/' + links.reports.bookingBuddyReport },
            { name: 'Top Service Report', href: links.reportLink + '/' + links.reports.topServiceReport },
            { name: 'Remake Item Report', href: links.reportLink + '/' + links.reports.remakeItemReport },
            { name: 'Profit Loss Report', href: links.reportLink + '/' + links.reports.profitLossReport },
            { name: 'Certificate Report', href: links.reportLink + '/' + links.reports.certificateReport },
            { name: 'Average Sales For Last 3 Month Report', href: links.reportLink + '/' + links.reports.averageSalesForLast3MonthReport },
            { name: 'Bonus Report', href: links.reportLink + '/' + links.reports.bonusReport },
            { name: 'Referral Sales Report', href: links.reportLink + '/' + links.reports.referralSalesReport },
            { name: 'Sales Chart', href: links.pagesLink + '/' + links.pages.salesChart },
            { name: 'Quota Busters by Month', href: links.pagesLink + '/' + links.pages.qbByMonth },
        ];

        this.actionLinks = [
            { name: 'DrivePipe', href: environment.pd },
            { name: 'Appointment Card', href: links.appointmentCardLink + '/' + links.appointmentCard.List },
            { name: 'Submit Payment', href: links.submitPaymentLink + '/' + links.submitPayment.List },
            { name: 'My Yearly goal', href: links.pagesLink + '/' + links.pages.myGoal },
            //     { name: 'Achievements', href: links.achievements },
            { name: 'WhatsApp Quiz Submissions', href: links.pagesLink + '/' + links.pages.whatsappQuizSubmissions },
            { name: 'WhatsApp Templates', href: links.pagesLink + '/' + links.pages.whatsappTemplates },
            { name: 'Clothier Career', href: links.pagesLink + '/' + links.pages.clothierCareer },
            { name: 'AI Chat', href: links.pagesLink + '/' + links.pages.aiChat },
        ];

        this.adminSettings = [
            { name: 'Item Management', href: '1' },
            { name: 'User Management', href: '2' },
            { name: 'Payments', href: '13' },
            { name: 'Survey Result', href: '3' },
            { name: 'Sales Management', href: '4' },
            { name: 'LGFG Certificates', href: '5' },
            { name: 'Payment Records', href: '21' },
            { name: 'Our Goal', href: '6' },
            { name: 'Exchange Rate to CAD', href: '9' },
            { name: 'Provinces and Cities', href: '10' },
            { name: 'Office management', href: '11' },
            { name: 'EA/Clothier Deposit Email Notifications', href: '7' },
            { name: 'Workspace Management', href: '22' },
            { name: 'Alteration Category Management', href: '12' },
            { name: 'Reassign Report', href: links.reportLink + '/' + links.reports.reassignReport },
            { name: 'Package Discount Management', href: '17' },
            { name: 'LGFG Companies Management Tab', href: '18' },
            { name: 'Growth awards YOY', href: '19' }
        ];
        if (this.context.isTechSupport()) {
            this.adminSettings.push({ name: 'Reassign Rule Management', href: '14' });
            this.adminSettings.push({ name: 'Logs', href: '8' });
            this.adminSettings.push({ name: 'Emails', href: '15' });
            this.adminSettings.push({ name: 'Remove Discount', href: '16' });
            this.adminSettings.push({ name: 'Hidden Users List', href: '20' });

            this.reportLinks.push({ name: 'QB Report', href: links.reportLink + '/' + links.reports.clothierQBReport });
            this.reportLinks.push({ name: 'New Clients Report', href: links.reportLink + '/' + links.reports.newClientReport });
            this.reportLinks.push({ name: 'Client Purchase Report', href: links.reportLink + '/' + links.reports.clientPurchaseReport });

            this.actionLinks.push({ name: 'Achievements', href: links.achievements });
        }
    }

    private setAccountingLinks(): void {
        this.reportLinks = [
            { name: 'My Sales Report', href: links.reportLink + '/' + links.reports.mySalesReport },
            { name: 'My Items Report', href: links.reportLink + '/' + links.reports.itemsReport },
            { name: 'My Cashflow Report', href: links.reportLink + '/' + links.reports.cashflowReport },
            { name: 'Total Clothing and Alterations Budget Bank Report', href: links.reportLink + '/' + links.reports.totalNCPBankReport },
            { name: 'My Alterations Report', href: links.reportLink + '/' + links.reports.alterationsReport },
            { name: 'My Expenses Report', href: links.reportLink + '/' + links.reports.expensesReport },
            { name: 'My Payments Report', href: links.reportLink + '/' + links.reports.paymentReport },
            { name: 'Rep Overdue Report', href: links.reportLink + '/' + links.reports.overdueReport },
            { name: 'Management Report', href: links.reportLink + '/' + links.reports.managementReport },
            { name: 'Rep Overview Report', href: links.reportLink + '/' + links.reports.overviewReport },
            { name: 'Cashflow Trailers Report', href: links.reportLink + '/' + links.reports.cashflowTrailorsReport },
            { name: 'SM Report', href: links.reportLink + '/' + links.reports.socialMediaReport },
            { name: 'BB Report', href: links.reportLink + '/' + links.reports.bookingBuddyReport },
            { name: 'Remake Item Report', href: links.reportLink + '/' + links.reports.remakeItemReport },
            { name: 'Profit Loss Report', href: links.reportLink + '/' + links.reports.profitLossReport },
            { name: 'Certificate Report', href: links.reportLink + '/' + links.reports.certificateReport },
            { name: 'Average Sales For Last 3 Month Report', href: links.reportLink + '/' + links.reports.averageSalesForLast3MonthReport },
            { name: 'Bonus Report', href: links.reportLink + '/' + links.reports.bonusReport },
            { name: 'Referral Sales Report', href: links.reportLink + '/' + links.reports.referralSalesReport },
            { name: 'Sales Chart', href: links.pagesLink + '/' + links.pages.salesChart },
            { name: 'Quota Busters by Month', href: links.pagesLink + '/' + links.pages.qbByMonth },
        ];

        this.actionLinks = null; /* [
            { name: 'Clothier Career', href: links.pagesLink + '/' + links.pages.clothierCareer },
        ];*/

        this.adminSettings = [
            { name: 'Item Management', href: '1' },
            { name: 'User Management', href: '2' },
            { name: 'Payments', href: '13' },
            { name: 'Sales Management', href: '4' },
            { name: 'LGFG Certificates', href: '5' },
            { name: 'Payment Records', href: '21' },
            { name: 'Exchange Rate to CAD', href: '9' },
            { name: 'Provinces and Cities', href: '10' },
            { name: 'Office management', href: '11' },
            { name: 'Alteration Category Management', href: '12' },
            { name: 'Package Discount Management', href: '17' },
        ];
        if (this.context.isCfo()) {
            this.adminSettings.push({ name: 'LGFG Companies Management Tab', href: '18' });
        }
    }

    private setEaLinks(): void {
        this.reportLinks = this.reportLinks.concat([
            { name: 'My Sales Report', href: links.reportLink + '/' + links.reports.mySalesReport },
            { name: 'Referral Sales Report', href: links.reportLink + '/' + links.reports.referralSalesReport }
        ]);

        this.actionLinks = this.actionLinks.concat([
            { name: 'DrivePipe', href: environment.pd },
            { name: 'Appointment Card', href: links.appointmentCardLink + '/' + links.appointmentCard.List },
            { name: 'Submit Payment', href: links.submitPaymentLink + '/' + links.submitPayment.List },
        ]);
    }

    private setSmLinks(): void {
        this.reportLinks = this.reportLinks.concat([
            { name: 'Cashflow Trailers Report', href: links.reportLink + '/' + links.reports.cashflowTrailorsReport },
            { name: 'SM Report', href: links.reportLink + '/' + links.reports.socialMediaReport }
        ]);

        this.actionLinks = this.actionLinks.concat([
            { name: 'DrivePipe', href: environment.pd },
            { name: 'Appointment Card', href: links.appointmentCardLink + '/' + links.appointmentCard.List },
        ]);
    }

    private setBbLinks(): void {
        this.reportLinks = this.reportLinks.concat([
            { name: 'Cashflow Trailers Report', href: links.reportLink + '/' + links.reports.cashflowTrailorsReport },
            { name: 'BB Report', href: links.reportLink + '/' + links.reports.bookingBuddyReport }
        ]);

        this.actionLinks = this.actionLinks.concat([
            { name: 'DrivePipe', href: environment.pd },
            { name: 'Appointment Card', href: links.appointmentCardLink + '/' + links.appointmentCard.List },
        ]);
    }

    private setRepLinks(): void {
        this.reportLinks = this.reportLinks.concat([
            { name: 'My Sales Report', href: links.reportLink + '/' + links.reports.mySalesReport },
            { name: 'My Items Report', href: links.reportLink + '/' + links.reports.itemsReport },
            { name: 'My Cashflow Report', href: links.reportLink + '/' + links.reports.cashflowReport },
            { name: 'Total Clothing and Alterations Budget Bank Report', href: links.reportLink + '/' + links.reports.totalNCPBankReport },
            { name: 'My Alterations Report', href: links.reportLink + '/' + links.reports.alterationsReport },
            { name: 'My Expenses Report', href: links.reportLink + '/' + links.reports.expensesReport },
            { name: 'My Payments Report', href: links.reportLink + '/' + links.reports.paymentReport },
            { name: 'Rep Overdue Report', href: links.reportLink + '/' + links.reports.overdueReport },
            { name: 'Rep Overview Report', href: links.reportLink + '/' + links.reports.overviewReport },
            { name: 'Cashflow Trailers Report', href: links.reportLink + '/' + links.reports.cashflowTrailorsReport },
            { name: 'Top Clients Report', href: links.reportLink + '/' + links.reports.topClientReport },
            { name: 'Bonus Report', href: links.reportLink + '/' + links.reports.bonusReport },
            { name: 'Referral Sales Report', href: links.reportLink + '/' + links.reports.referralSalesReport },
            { name: 'Quota Busters by Month', href: links.pagesLink + '/' + links.pages.qbByMonth },
        ]);

        this.actionLinks = this.actionLinks.concat([
            { name: 'DrivePipe', href: environment.pd },
            { name: 'Submit Payment', href: links.submitPaymentLink + '/' + links.submitPayment.List },
            { name: 'My Yearly goal', href: links.pagesLink + '/' + links.pages.myGoal },
            //    { name: 'Achievements', href: links.achievements },
            //   { name: 'Clothier Career', href: links.pagesLink + '/' + links.pages.clothierCareer }
        ]);
    }

    private setSideBarState(): void {
        const array = window.location.href.split('/');
        const page = array[array.length - 1];
        const domen = array[array.length - 2];
        switch (domen) {
            case 'Report': this.tabInFocus = NavTabEnum.report;
                this.currentPage = this.reportLinks.find(x => x.href.includes(page))?.href;
                break;
            case 'SubmitPayment': this.tabInFocus = NavTabEnum.actions;
                this.currentPage = this.actionLinks.find(x => x.name == 'Submit Payment').href;
                break;
            case 'AppointmentCard': this.tabInFocus = NavTabEnum.actions;
                this.currentPage = this.actionLinks.find(x => x.name == 'Appointment Card').href;
                break;
            case 'documents': this.tabInFocus = NavTabEnum.documents; break;
            case '#': if (page == "MyGoal") {
                this.tabInFocus = NavTabEnum.actions;
                this.currentPage = this.actionLinks.find(x => x.name == 'My Yearly goal').href;
            } break;
            case 'Pages':
                if (page == 'ClothierCareer') {
                    this.tabInFocus = NavTabEnum.actions;
                    this.currentPage = this.actionLinks.find(x => x.name == 'Clothier Career')?.href;
                }
                break;
            case 'AdminSettings': this.tabInFocus = NavTabEnum.administration;
                this.currentPage = this.adminSettings.find(x => x.href == page.split('Id=')[1])?.href
                break;
            default: this.tabInFocus = NavTabEnum.dashboard; break;
        }
    }
}
